import type { FocusEvent, RefObject } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";

import { isTiptapDocumentBlank } from "@/react/components/CommentsCompass/helpers";
import type { TiptapFormValues } from "@/react/components/CommentsCompass/types";

interface UseComposerCollapseParams {
  isCollapsible: boolean;
  wrapperRef: RefObject<HTMLDivElement | null>;
}

export const useComposerCollapse = ({
  wrapperRef,
  isCollapsible,
}: UseComposerCollapseParams) => {
  const { getValues } = useFormContext<TiptapFormValues>();
  const isInteractionInsideRef = useRef(false);
  // Start open when the form mounts with a persisted draft.
  const [isOpen, setIsOpen] = useState(
    () => !isTiptapDocumentBlank(getValues("tiptap_body.body")),
  );

  // Blur alone can't tell "left the composer" from "focus moved into portalled
  // toolbar UI" (emoji picker, GIF/image modals) — but the latter is always
  // initiated by a pointerdown or key activation inside the composer. Tab is
  // navigation, never activation, so it always re-arms collapsing.
  useEffect(() => {
    if (!isCollapsible) return;

    const isInside = (event: Event) =>
      event.target instanceof Node &&
      !!wrapperRef.current?.contains(event.target);

    const onPointerDown = (event: PointerEvent) => {
      isInteractionInsideRef.current = isInside(event);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      isInteractionInsideRef.current = event.key !== "Tab" && isInside(event);
    };

    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [isCollapsible, wrapperRef]);

  // Submitting remounts the editor, and removing a focused node doesn't fire
  // `blur` — collapse must be triggered explicitly.
  const collapse = useCallback(() => setIsOpen(false), []);

  if (!isCollapsible) {
    return {
      isCollapsed: false,
      collapse,
      onFocus: undefined,
      onBlur: undefined,
    };
  }

  const onFocus = () => setIsOpen(true);

  const onBlur = (event: FocusEvent<HTMLDivElement>) => {
    const next = event.relatedTarget;
    if (next && wrapperRef.current?.contains(next)) return;
    if (isInteractionInsideRef.current) return;
    if (isTiptapDocumentBlank(getValues("tiptap_body.body"))) setIsOpen(false);
  };

  return {
    isCollapsed: !isOpen,
    collapse,
    onFocus,
    onBlur,
  };
};
